import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import { showErrorMessage } from '@acorn-portal/shared/src/components/toast/messageUtils';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useGetMileageOptions from '@acorn-portal/shared/src/hooks/useGetMileageOptions';
import useNoChangeWarning from '@acorn-portal/shared/src/hooks/useNoChangeWarning';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import {
	PrivateCarClient,
	Proposer,
	UpdatePrivateCarQuoteCommand,
} from '@acorn-portal/shared/src/services/subscriptionAPI';
import { updateSubscriptionData } from '@acorn-portal/shared/src/store/subscription.slice';
import { updateChangeOfUsage } from '@acorn-portal/shared/src/store/subscriptionChanges.slice';
import { isUsedForBusiness } from '@acorn-portal/shared/src/utils';
import {
	ANNUAL_MILEAGE,
	BUSINESS_MILEAGE,
	COVER_LEVEL_RESTART,
	VEHICLE_USED_FOR,
} from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Box, Collapse } from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { changeOfUsageOrMileageSchema } from '../validation/change-of-mileage-usage-schema';
import { IChangeUsageOrMileageForm, getUsageCoverDetails } from './utils/get-usage-cover-details';
import { getUsageDetails } from './utils/get-usage-details';
import { getMileageUsageInitialValues } from './utils/get-usage-initial-values';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

const AmendUsageOrMileage = () => {
	const [open, toggleModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const lists = useAppSelector((state) => state.referenceData);
	const subscriptionData = useAppSelector((state) => state.subscription);
	const quoteData = useMemo(() => subscriptionData.quoteData, [subscriptionData.quoteData]);
	const vehicleAmendDraft = useMemo(
		() => subscriptionData.quoteData?.quoteDetails.riskData.vehicleDetails,
		[subscriptionData.quoteData?.quoteDetails.riskData.vehicleDetails],
	);
	const [hasChanged] = useNoChangeWarning();
	const { annualMileageOptions, getBusinessMileageOptions } = useGetMileageOptions();

	useEffect(() => {
		if (!quoteData?.quoteResponse.quoteRef) {
			navigate(ROUTE_PATHS.home);
		}
	}, [quoteData?.quoteResponse.quoteRef, navigate]);

	const initialValues = useMemo(
		() => getMileageUsageInitialValues(quoteData, annualMileageOptions),
		[quoteData, annualMileageOptions],
	);

	const onSubmit = async (values: IChangeUsageOrMileageForm) => {
		if (!hasChanged(initialValues, values)) {
			showErrorMessage('You are unable to proceed, as you did not make any changes to your policy.');
			return;
		}

		setLoading(true);

		try {
			const baseVehicleDetails = vehicleAmendDraft;
			if (!baseVehicleDetails) throw new Error('Vehicle details not available');
			const vehicleDetails = getUsageDetails(values, baseVehicleDetails);
			const coverDetails = getUsageCoverDetails(values, quoteData);
			const url = import.meta.env.VITE_SUBSCRIPTION_API;
			const subscriptionClient = new PrivateCarClient(url, API);

			const body: UpdatePrivateCarQuoteCommand = {
				quoteReference: quoteData?.quoteResponse.quoteRef,
				quoteToken: quoteData?.quoteToken,
				quoteDetails: {
					...quoteData?.quoteDetails,
					effectiveFrom: quoteData?.quoteDetails.effectiveFrom || new Date().toISOString(),
					riskData: {
						...quoteData?.quoteDetails.riskData,
						vehicleDetails: vehicleDetails || quoteData?.quoteDetails.riskData.vehicleDetails,
						coverDetails,
						driverDetails: quoteData?.quoteDetails.riskData.driverDetails || [],
						proposerDetails: quoteData?.quoteDetails.riskData.proposerDetails || ({} as Proposer),
					},
				},
			};
			const response = await subscriptionClient.updateQuote(body);

			if (response.quoteResponse.underwrittenOptions?.[0]?.status === 'Declined') {
				navigate(ROUTE_PATHS.subscriptionRestartReferral);
				return;
			}

			dispatch(
				updateSubscriptionData({
					policyToken: subscriptionData.policyToken ?? '',
					effectiveDate: subscriptionData.effectiveDate ?? '',
					quoteData: response,
					sendWelcomePackStatus: undefined,
					autoRenew: undefined,
				}),
			);

			dispatch(updateChangeOfUsage(true));

			navigate(`${ROUTE_PATHS.subscriptionRestartSummary}/${subscriptionData.policyToken}`);
		} catch (error) {
			console.error('Error updating quote:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	return (
		<JourneyCardLayout pageTitle='Change of Usage & Mileage' loading={loading}>
			<Formik<IChangeUsageOrMileageForm>
				validationSchema={changeOfUsageOrMileageSchema()}
				initialValues={initialValues}
				onSubmit={onSubmit}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						<ToggleButtonFormField
							label='What will you be using the vehicle for?'
							name='vehicleUse'
							id='vehicleUse'
							value={formik.values.vehicleUse}
							onChange={(value) => {
								formik.setFieldValue('vehicleUse', value);
								if (!isUsedForBusiness(value)) {
									formik.setFieldValue('businessMileage', undefined);
								}
							}}
							options={lists.vehicleUseOptions as IOptions[]}
							error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
							helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
							modal={{
								title: VEHICLE_USED_FOR.header,
								description: VEHICLE_USED_FOR.text,
							}}
							product={Product.PRIVATE_CAR}
						/>
						<SelectFormField
							label='Annual mileage'
							name='annualMileage'
							id='annualMileage'
							data-testid='annual-mileage'
							value={formik.values.annualMileage}
							onChange={(value) => {
								formik.setFieldValue('annualMileage', value);
							}}
							options={lists.millageIncrementOptions as IOptions[]}
							modal={{
								title: ANNUAL_MILEAGE.header,
								description: ANNUAL_MILEAGE.text,
							}}
							error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
							helperText={formik.touched.annualMileage && formik.errors.annualMileage}
						/>
						<Collapse in={isUsedForBusiness(formik.values.vehicleUse)}>
							<Box>
								<SelectFormField
									label='Business mileage'
									name='businessMileage'
									id='businessMileage'
									value={formik.values.businessMileage}
									onChange={(value) => {
										formik.setFieldValue('businessMileage', value);
									}}
									options={getBusinessMileageOptions(formik.values.annualMileage) as IOptions[]}
									modal={{
										title: BUSINESS_MILEAGE.header,
										description: BUSINESS_MILEAGE.text,
									}}
									error={formik.touched.businessMileage && Boolean(formik.errors.businessMileage)}
									helperText={formik.touched.businessMileage && formik.errors.businessMileage}
								/>
							</Box>
						</Collapse>
						<SelectFormField
							label='What level of cover do you require?'
							name='levelOfCover'
							id='levelOfCover'
							value={formik.values.levelOfCover}
							onChange={(value) => formik.setFieldValue('levelOfCover', value)}
							options={lists.levelOfCoverTypes.filter((x) => x.value !== 'TPO') as IOptions[]}
							error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
							helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
							modal={{
								title: COVER_LEVEL_RESTART.header,
								description: COVER_LEVEL_RESTART.text,
							}}
						/>
						<ActionButtons>
							<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
						<HelperModal
							open={open}
							onClose={() => {
								toggleModal(false);
							}}
							title='Discard The Changes?'
							description='Are you sure you wish to discard the changes?'
							closeText='No'
							submitText='Yes'
							onSubmit={() => {
								formik.resetForm();
								navigate(-1);
							}}
						/>
					</Form>
				)}
			</Formik>
		</JourneyCardLayout>
	);
};

export default AmendUsageOrMileage;
